/**
 * PinDialog — small dialog that asks for an editor PIN and, on success,
 * transitions the Learn page into "edit mode".
 *
 * Uses POST /api/pp/auth/user-pin with section="learn_edit".
 */
import { useEffect, useRef, useState } from "react";
import { X, KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { loginWithPin } from "@/lib/learn/api";

export default function PinDialog({ onClose, onAuthed }) {
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await loginWithPin(pin.trim());
      if (res.ok) {
        onAuthed?.(res.user);
      } else {
        const reasonMap = {
          not_found: "PIN not recognised.",
          inactive: "This account is inactive.",
          no_permission: "You don't have permission to edit the video library.",
          pin_format: "PIN must be 4–8 characters.",
        };
        setError(reasonMap[res.reason] || "Invalid PIN.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="learn-pin-dialog"
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 h-9 w-9 rounded-full text-slate-400 hover:text-slate-900 hover:bg-slate-100 flex items-center justify-center transition"
          aria-label="Close"
          data-testid="learn-pin-close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="h-12 w-12 rounded-full bg-brand-mist flex items-center justify-center text-brand-navy mb-4">
          <KeyRound className="h-5 w-5" />
        </div>
        <h2 className="text-2xl font-bold font-heading text-brand-navy">Editor sign-in</h2>
        <p className="mt-1 text-sm text-slate-500">
          Enter your HepCURE PIN to manage the video library.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">PIN</span>
            <div className="mt-1.5 relative">
              <input
                ref={inputRef}
                type={showPin ? "text" : "password"}
                inputMode="numeric"
                autoComplete="off"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="w-full h-12 rounded-lg border border-slate-200 bg-white px-4 pr-11 text-lg tracking-widest text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal"
                data-testid="learn-pin-input"
              />
              <button
                type="button"
                onClick={() => setShowPin((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md text-slate-400 hover:text-brand-navy hover:bg-slate-100 flex items-center justify-center"
                aria-label={showPin ? "Hide PIN" : "Show PIN"}
                tabIndex={-1}
              >
                {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </label>

          {error && (
            <p className="text-sm text-red-600" data-testid="learn-pin-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || !pin.trim()}
            className="w-full h-12 rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition"
            data-testid="learn-pin-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {busy ? "Signing in…" : "Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
